import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';
import Image from '../AppImage';

const PrimaryNavigation = ({ userRole = 'clinician', creditBalance = 0, subscriptionTier = 'Free' }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { profile, signOut } = useAuth();

  const navigationItems = [
  {
    label: t('navigation.newRecommendation'),
    path: '/clinical-recommendation',
    icon: 'FileText',
    roles: ['clinician', 'admin'],
    description: t('navigation.newRecommendationDesc')
  },
  {
    label: t('navigation.guidelines'),
    path: '/guidelines-reference',
    icon: 'BookOpen',
    roles: ['clinician', 'admin'],
    description: t('navigation.guidelinesDesc')
  },
  {
    label: 'Search Guidelines',
    path: '/search-guidelines',
    icon: 'Search',
    roles: ['clinician', 'admin'],
    description: 'Search iRefer clinical guidelines'
  },
  {
    label: 'Chat with Technologist',
    path: '/doctor-chat',
    icon: 'MessageCircle',
    roles: ['clinician', 'admin'],
    description: 'Real-time chat with diagnostic imaging technologists'
  },
  {
    label: t('navigation.tutorials'),
    path: '/user-guide',
    icon: 'GraduationCap',
    roles: ['clinician', 'admin'],
    description: t('navigation.tutorialsDesc')
  },
  {
    label: t('navigation.settings'),
    path: '/account-settings',
    icon: 'Settings',
    roles: ['clinician', 'admin'],
    description: t('navigation.settingsDesc')
  },
  {
    label: 'Admin Dashboard',
    path: '/admin-dashboard',
    icon: 'Shield',
    roles: ['admin'],
    description: 'Administrative controls and user management'
  }];


  const visibleItems = navigationItems?.filter((item) => item?.roles?.includes(userRole));

  const isActive = (path) => location?.pathname === path;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 h-16 sm:h-18 md:h-20 bg-card border-b border-border z-50 shadow-sm">
        <div className="h-full px-3 sm:px-4 md:px-6 lg:px-8 flex items-center justify-between max-w-[1920px] mx-auto">
          <div className="flex items-center gap-2 sm:gap-4 md:gap-6 lg:gap-8 flex-1 min-w-0">
            <Link to="/dashboard" className="flex items-center gap-2 flex-shrink-0">
              <Image
                src="/assets/images/Screenshot_2025-12-22_at_12.13.42_AM-1766377944108.png"
                alt="My Doc Refer logo"
                className="h-8 sm:h-10 md:h-12 lg:h-14 w-auto object-contain" />

            </Link>

            {/* Desktop Navigation - Optimized for larger screens */}
            <nav className="hidden lg:flex items-center gap-1 xl:gap-2 overflow-x-auto scrollbar-hide flex-1">
              {visibleItems?.map((item) =>
              <Link
                key={item?.path}
                to={item?.path}
                className={`flex items-center gap-2 px-3 xl:px-4 py-2 rounded-md text-sm font-medium transition-clinical whitespace-nowrap ${
                isActive(item?.path) ?
                'bg-primary text-primary-foreground' :
                'text-muted-foreground hover:bg-muted hover:text-foreground'}`
                }
                title={item?.description}>

                  <Icon name={item?.icon} size={18} />
                  <span className="hidden xl:inline">{item?.label}</span>
                </Link>
              )}
            </nav>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 md:gap-4 flex-shrink-0">
            {/* Credits Display - Responsive sizing */}
            <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 px-2 sm:px-3 md:px-4 py-1.5 md:py-2 bg-muted rounded-lg">
              <Icon name="Coins" size={16} color="var(--color-accent)" />
              <div className="flex flex-col">
                <span className="text-[10px] sm:text-xs text-muted-foreground hidden sm:block">Credits</span>
                <span className="text-xs sm:text-sm font-mono font-medium text-foreground">{creditBalance || profile?.credits_remaining || 0}</span>
              </div>
            </div>

            {/* Subscription Tier Badge - Hidden on small screens */}
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-md">
              <Icon name="Crown" size={16} color="var(--color-primary)" />
              <span className="text-xs font-medium text-primary">{subscriptionTier || profile?.subscription_tier || 'Free'}</span>
            </div>

            {/* User Menu with visible username */}
            <div className="relative">
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 hover:bg-muted rounded-lg transition-all duration-200 group touch-manipulation">

                <div className="relative">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-br from-primary to-accent text-white rounded-lg flex items-center justify-center font-bold text-xs sm:text-sm shadow-md group-hover:shadow-lg transition-all duration-200 group-hover:scale-105">
                    {profile?.full_name?.charAt(0) || 'U'}
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 sm:w-2.5 sm:h-2.5 bg-success rounded-full border-2 border-white"></div>
                </div>
                {/* Username visible on all devices */}
                <div className="flex flex-col items-start min-w-0">
                  <span className="text-xs sm:text-sm font-semibold text-foreground truncate max-w-[80px] sm:max-w-[120px] md:max-w-[140px]">
                    {profile?.full_name || 'User'}
                  </span>
                  <span className="hidden sm:block text-[10px] text-muted-foreground">
                    {profile?.subscription_tier || subscriptionTier || 'Free'}
                  </span>
                </div>
                <Icon
                  name={isUserMenuOpen ? "ChevronUp" : "ChevronDown"}
                  size={16}
                  color="var(--color-muted-foreground)"
                  className="hidden sm:block transition-transform duration-200" />

              </button>
              
              {/* Dropdown Menu */}
              {isUserMenuOpen &&
              <>
                  <div
                  className="fixed inset-0 z-40"
                  onClick={() => setIsUserMenuOpen(false)} />

                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-border z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                    {/* User Info */}
                    <div className="p-4 bg-gradient-to-br from-primary/5 to-accent/5 border-b border-border">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent text-white rounded-lg flex items-center justify-center font-bold shadow-md">
                          {profile?.full_name?.charAt(0) || 'U'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-foreground truncate">
                            {profile?.full_name || 'User'}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {profile?.email || ''}
                          </p>
                          <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 rounded-full">
                            <Icon name="Crown" size={10} color="var(--color-primary)" />
                            <span className="text-[10px] font-medium text-primary">
                              {profile?.subscription_tier || subscriptionTier || 'Free'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Menu Items */}
                    <div className="p-2">
                      <button
                      onClick={() => {
                        navigate('/account-settings');
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full text-left px-3 py-2.5 text-sm hover:bg-muted rounded-lg transition-colors flex items-center gap-3 group touch-manipulation">

                        <Icon name="Settings" size={16} className="text-muted-foreground group-hover:text-foreground transition-colors" />
                        <span className="font-medium">{t('userMenu.accountSettings')}</span>
                      </button>
                      <button
                      onClick={() => {
                        navigate('/purchase-credits');
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full text-left px-3 py-2.5 text-sm hover:bg-muted rounded-lg transition-colors flex items-center gap-3 group touch-manipulation">

                        <Icon name="CreditCard" size={16} className="text-muted-foreground group-hover:text-foreground transition-colors" />
                        <span className="font-medium">{t('userMenu.purchaseCredits')}</span>
                      </button>
                      <div className="my-2 border-t border-border"></div>
                      <button
                      onClick={() => {
                        signOut();
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full text-left px-3 py-2.5 text-sm text-destructive hover:bg-destructive/10 rounded-lg transition-colors flex items-center gap-3 group touch-manipulation">

                        <Icon name="LogOut" size={16} />
                        <span className="font-medium">{t('userMenu.signOut')}</span>
                      </button>
                    </div>
                  </div>
                </>
              }
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 rounded-md hover:bg-muted transition-clinical focus-clinical"
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}>

              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu - Optimized slide-in panel */}
      {isMobileMenuOpen &&
      <div className="fixed inset-0 top-16 bg-background z-40 lg:hidden animate-fade-in overflow-y-auto">
          <nav className="flex flex-col p-4 gap-2 max-w-md mx-auto">
            {visibleItems?.map((item) =>
          <Link
            key={item?.path}
            to={item?.path}
            onClick={() => setIsMobileMenuOpen(false)}
            className={`flex items-start gap-3 px-4 py-3 rounded-lg text-base font-medium transition-clinical ${
            isActive(item?.path) ?
            'bg-primary text-primary-foreground' :
            'text-foreground hover:bg-muted'}`
            }>

                <Icon name={item?.icon} size={20} className="mt-0.5 flex-shrink-0" />
                <div className="flex flex-col gap-1 min-w-0">
                  <span>{item?.label}</span>
                  <span className="text-xs text-muted-foreground opacity-80">{item?.description}</span>
                </div>
              </Link>
          )}

            {/* Mobile Credits & Subscription Display */}
            <div className="mt-4 pt-4 border-t border-border space-y-3">
              <div className="flex items-center justify-between px-4 py-3 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <Icon name="Coins" size={20} color="var(--color-accent)" />
                  <span className="text-sm text-muted-foreground">Credits Available</span>
                </div>
                <span className="text-base font-mono font-medium text-foreground">{creditBalance}</span>
              </div>

              <div className="flex items-center justify-between px-4 py-2 bg-primary/10 rounded-lg">
                <div className="flex items-center gap-2">
                  <Icon name="Crown" size={18} color="var(--color-primary)" />
                  <span className="text-sm font-medium text-primary">Current Plan</span>
                </div>
                <span className="text-sm font-medium text-primary">{subscriptionTier}</span>
              </div>
            </div>
          </nav>
        </div>
      }
    </>
  );

};

export default PrimaryNavigation;